import { useCallback, useSyncExternalStore } from 'react';
import type { ThemeName } from './types';

const STORAGE_KEY = 'faim2-theme';
const DEFAULT_THEME: ThemeName = 'theme-light';
const THEME_CLASSES: ThemeName[] = ['theme-light', 'theme-dark'];

let listeners: Array<() => void> = [];

function getSnapshot(): ThemeName {
  return (localStorage.getItem(STORAGE_KEY) as ThemeName) || DEFAULT_THEME;
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function applyThemeToDOM(theme: ThemeName) {
  const root = document.documentElement;
  THEME_CLASSES.forEach((cls) => root.classList.remove(cls));
  root.classList.add(theme);
}

// Apply on module load so there's no flash
applyThemeToDOM(getSnapshot());

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot);

  const setTheme = useCallback((next: ThemeName) => {
    localStorage.setItem(STORAGE_KEY, next);
    applyThemeToDOM(next);
    listeners.forEach((l) => l());
  }, []);

  const toggleTheme = useCallback(() => {
    const current = getSnapshot();
    setTheme(current === 'theme-light' ? 'theme-dark' : 'theme-light');
  }, [setTheme]);

  return { theme, setTheme, toggleTheme } as const;
}
